// ─── FD (Facility Decommission) Workflow Models (M009) ───────────────────────

// ─── Stage constants ─────────────────────────────────────────────────────────

/** Stage values for the FD workflow pipeline. */
export const FdStage = {
  ProjectDataEntry:   'ProjectDataEntry',
  LandReview:         'LandReview',
  EnvironmentalReview:'EnvironmentalReview',
  SiteScoping:        'SiteScoping',
  SiteReview:         'SiteReview',
  AfeReview:          'AfeReview',
  EamAndPermitting:   'EamAndPermitting',
  DigPlan:            'DigPlan',
  Scheduling:         'Scheduling',
  JobExecution:       'JobExecution',
  JobCloseOut:        'JobCloseOut',
  Resolved:           'Resolved',
} as const;

export type FdStageValue = (typeof FdStage)[keyof typeof FdStage];

/** Dig Plan sub-process values (ADR-M9-003). */
export const FdDigPlanSubProcess = {
  Creation:  'Creation',
  Approval:  'Approval',
  Execution: 'Execution',
} as const;

export type FdDigPlanSubProcessValue = (typeof FdDigPlanSubProcess)[keyof typeof FdDigPlanSubProcess];

/** Resolved outcome values. */
export const FdResolvedOutcome = {
  Completed: 'Resolved-Completed',
  Error:     'Resolved-Error',
  NotNeeded: 'Resolved-NotNeeded',
} as const;

/** Cancellation reason options for the Cancel Case dialog (FR-033). */
export const FdCancellationReason = {
  DuplicateHumanError: 'Duplicate/Human Error',
  NotNeeded:           'Not Needed',
} as const;

// ─── List / Page ──────────────────────────────────────────────────────────────

export interface FdCaseListQuery {
  stage?: string;
  keyword?: string;
  fot?: string;
  scheduleCrew?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface FdCaseListItem {
  id: number;
  caseNumber: string;
  facilityName: string;
  fot: string;
  stage: string;
  /** True when Environmental Review was submitted independently at LandReview stage. */
  isEnvironmentalReviewSubmitted?: boolean;
  /** True when EAM sub-stage was submitted (IsEamWorkOrdersCompleted). */
  isEamSubmitted?: boolean;
  /** True when Project Permitting sub-stage was submitted (BP email exists). */
  isProjectPermittingSubmitted?: boolean;
  createdDate: string;
  updatedDate: string;
  createdBy: string | null;
  scheduleCrew: string | null;
  startDate: string | null;
  projEndDate: string | null;
  projectedJobDays: number | null;
  deadlineDate: string | null;
  queueOrder: number | null;
  isSetDatesManually?: boolean;
  /** Net revenue interest percentage — sourced from FdLandReview (Land dashboard). */
  netRevenueInterestPercent?: string | null;
  /** Working interest percentage — sourced from FdLandReview (Land dashboard). */
  workingInterestPercent?: string | null;
}

export interface FdCasePage {
  totalCount: number;
  items: FdCaseListItem[];
}

export interface FdHistoricalListItem {
  id: number;
  caseNumber: string;
  facilityName: string;
  fot: string;
  resolvedOutcome: string | null;
  completedDate: string | null;
}

export interface FdHistoricalPage {
  totalCount: number;
  items: FdHistoricalListItem[];
}

// ─── Case detail ──────────────────────────────────────────────────────────────

export interface FdCaseDetail {
  id: number;
  workflowCaseId: number;
  caseNumber: string;
  stage: string;
  /** True when env review was submitted independently (at LandReview stage). */
  isEnvironmentalReviewSubmitted: boolean;
  resolvedOutcome: string | null;
  cancellationReason: string | null;
  cancellationComments: string | null;
  facilityName: string;
  facilityId: string | null;
  chevNo: string | null;
  fot: string;
  state: string;
  section: string | null;
  township: string | null;
  range: string | null;
  survey: string | null;
  groundElevation: number | null;
  latitude: number | null;
  longitude: number | null;
  landOwnership: string | null;
  reasonForDecommission: string | null;
  directionsToFacility: string | null;
  wells: FdWell[];
  equipment: FdEquipment[];
  contacts: FdContact[];
  wbsAllocations: FdWbsAllocation[];
  businessPartnerEmails: FdBusinessPartnerEmail[];
  landReview: FdLandReviewStage | null;
  environmentalReview: FdEnvironmentalReviewStage | null;
  siteScoping: FdSiteScopingStage | null;
  siteReview: FdSiteReviewStage | null;
  afeReview: FdAfeReviewStage | null;
  eam: FdEamStage | null;
  projectPermitting: FdProjectPermittingStage | null;
  digPlan: FdDigPlanStage | null;
  scheduling: FdSchedulingStage | null;
  jobExecution: FdJobExecutionStage | null;
  jobCloseOut: FdJobCloseOutStage | null;
  createdDate: string;
  createdBy: string | null;
  updatedDate: string;
  updatedBy: string | null;
}

// ─── Child rows ───────────────────────────────────────────────────────────────

export interface FdWell {
  id: number;
  wellName: string | null;
  api14: string | null;
  fot: string | null;
  area: string | null;
}

export interface FdEquipment {
  id: number;
  description: string | null;
  count: number | null;
  isReusable: boolean;
  comments: string | null;
}

export interface FdContact {
  id: number;
  name: string | null;
  role: string | null;
  phoneNumber: string | null;
  email: string | null;
}

export interface FdWbsAllocation {
  id: number;
  wbsNumber: string | null;
  allocationPercent: number | null;
}

export interface FdBusinessPartnerEmail {
  id: number;
  email: string;
}

// ─── Per-stage embedded data ──────────────────────────────────────────────────

export interface FdLandReviewStage {
  isLwolLwou: boolean;
  copCriticalDate: string | null;
  isSurfaceOwnerNotificationRequired: boolean;
  isWorkingInterestOwnerApprovalRequired: boolean;
  workingInterestPercent: number | null;
  netRevenueInterestPercent: number | null;
  hasAdditionalParties: boolean;
  landComment: string | null;
}

export interface FdEnvironmentalReviewStage {
  hasOpenReleaseSpill: boolean;
  releaseSpillDetails: string | null;
  hasOpenRemediationProject: boolean;
  remediationProjectDetails: string | null;
  isEsaChecksCompleted: boolean;
  pointOfContact: string | null;
  environmentalDetails: string | null;
}

export interface FdSiteScopingStage {
  samplingDetails: string | null;
  environmentalImpacts: string | null;
  subtotal: number | null;
  contingency: number | null;
  gaPercent: number | null;
  gaCost: number | null;
  totalCost: number | null;
}

export interface FdSiteReviewStage {
  scopeReviewApproval: string | null;
  scopeReviewComments: string | null;
  updatedDate: string | null;
}

export interface FdAfeReviewStage {
  highLevelEstimate: number | null;
  aesCostEstimate: number | null;
  aefCostEstimate: number | null;
  approvedAes: number | null;
  approvedAef: number | null;
  companyCode: string | null;
  afeLink: string | null;
  wbsComments: string | null;
}

export interface FdEamStage {
  isEamWorkOrdersCompleted: boolean;
}

export interface FdProjectPermittingStage {
  isAgenciesNeedNotification: boolean;
  isSubmitted: boolean;
}

export interface FdDigPlanStage {
  subProcess: string;
  digPlanApprovalStatus: string | null;
  approvalComments: string | null;
  rejectionComments: string | null;
  isDigPlanCompleted: boolean;
  approverUserId: number | null;
  approverDisplayName: string | null;
  rejectedOnUtc: string | null;
}

export interface FdSchedulingStage {
  isSetDatesManually: boolean;
  startDate: string | null;
  projEndDate: string | null;
  scheduleCrew: string | null;
  queueOrder: number | null;
  projectedJobDays: number | null;
  deadlineDate: string | null;
}

export interface FdJobExecutionStage {
  jobCompletionDate: string | null;
  needsFacilityReclamation: boolean | null;
  reclamationCloseOutComments: string | null;
}

export interface FdJobCloseOutStage {
  isAgenciesNotifiedOfCompletion: boolean;
  isExecuteFacilityReclamation: boolean | null;
}

// ─── Request payloads (save/submit) ──────────────────────────────────────────

export interface CreateFdCaseRequest {
  workflowCaseId: number;
}

export interface CreateFdCaseResponse {
  id: number;
  caseNumber: string;
}

export interface FdCancelCaseRequest {
  cancellationReason: string;
  cancellationComments: string;
}

export interface FdSaveProjectDataEntryRequest {
  facilityName?: string;
  facilityId?: string | null;
  chevNo?: string | null;
  fot?: string;
  state?: string;
  section?: string | null;
  township?: string | null;
  range?: string | null;
  survey?: string | null;
  groundElevation?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  landOwnership?: string | null;
  reasonForDecommission?: string | null;
  directionsToFacility?: string | null;
}

export interface FdSaveLandReviewRequest {
  isLwolLwou: boolean;
  copCriticalDate: string | null;
  isSurfaceOwnerNotificationRequired: boolean;
  isWorkingInterestOwnerApprovalRequired: boolean;
  workingInterestPercent: number | null;
  netRevenueInterestPercent: number | null;
  hasAdditionalParties: boolean;
  landComment: string | null;
}

export interface FdSaveEnvironmentalReviewRequest {
  hasOpenReleaseSpill: boolean;
  releaseSpillDetails: string | null;
  hasOpenRemediationProject: boolean;
  remediationProjectDetails: string | null;
  isEsaChecksCompleted: boolean;
  pointOfContact: string | null;
  environmentalDetails: string | null;
}

export interface FdSaveSiteScopingRequest {
  samplingDetails: string | null;
  environmentalImpacts: string | null;
  subtotal: number | null;
  contingency: number | null;
}

export interface FdSiteReviewDecisionRequest {
  decision: 'Approved' | 'Rejected';
  scopeReviewComments: string | null;
}

export interface FdSaveAfeReviewRequest {
  aesCostEstimate: number | null;
  aefCostEstimate: number | null;
  approvedAes: number | null;
  approvedAef: number | null;
  companyCode: string | null;
  afeLink: string | null;
  wbsComments: string | null;
}

export interface FdSaveEamRequest {
  isEamWorkOrdersCompleted: boolean;
}

export interface FdSaveProjectPermittingRequest {
  isAgenciesNeedNotification: boolean;
}

export interface FdDigPlanApprovalRequest {
  decision: 'Approved' | 'Rejected';
  approvalComments: string | null;
  rejectionComments: string | null;
  approverUserId: number | null;
}

export interface FdSaveDigPlanApprovalRequest {
  approvalStatus: string | null;
  approvalComments: string | null;
  rejectionComments: string | null;
}

export interface FdSaveDigPlanExecutionRequest {
  isDigPlanCompleted: boolean;
}

export interface FdSaveSchedulingRequest {
  isSetDatesManually: boolean;
  startDate: string | null;
  projEndDate: string | null;
  scheduleCrew: string | null;
  queueOrder: number | null;
  projectedJobDays: number | null;
  deadlineDate: string | null;
}

export interface FdSaveJobExecutionRequest {
  jobCompletionDate: string | null;
  needsFacilityReclamation: boolean | null;
  reclamationCloseOutComments: string | null;
}

export interface FdSaveJobCloseOutRequest {
  isAgenciesNotifiedOfCompletion: boolean;
  isExecuteFacilityReclamation: boolean | null;
}

// ─── Child-row request payloads ───────────────────────────────────────────────

export interface FdSaveWellRequest {
  wellName: string | null;
  api14: string | null;
  fot: string | null;
  area: string | null;
}

export interface FdSaveEquipmentRequest {
  description: string | null;
  count: number | null;
  isReusable: boolean;
  comments: string | null;
}

export interface FdSaveContactRequest {
  name: string | null;
  role: string | null;
  phoneNumber: string | null;
  email: string | null;
}

export interface FdSaveWbsAllocationRequest {
  wbsNumber: string | null;
  allocationPercent: number | null;
}

export interface FdSaveBusinessPartnerEmailRequest {
  email: string;
}

// ─── Validation error ─────────────────────────────────────────────────────────

export interface FdValidationError {
  field: string;
  message: string;
}

export interface FdAttachment {
  id: number;
  caseId: number;
  attachmentType: string;
  fileName: string;
  uploadedDate: string;
  uploadedBy: string | null;
  fileSizeBytes: number;
  /** Semicolon-separated recipients; present only for Correspondence type rows. */
  recipients?: string | null;
  /** Email subject line from NotificationLog; present only for Correspondence type rows. */
  subject?: string | null;
}

// --- Scheduling Queue (WS-FD-SCHED-001) ---
export interface FdSchedulingQueueRow {
  id: number;
  fdCaseId: number;
  queueOrder: number;
  isSetDatesManually: boolean;
  startDate: string | null;
  projEndDate: string | null;
  projectedJobDays: number | null;
}
